import { useTheme } from "@emotion/react";
import { Box } from "@mui/material";
import React, { useEffect } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolBar";
import { fetchLocations } from "reduxStore/actions/locationActions";
import moment from "moment";
import { EditOutlined } from "@mui/icons-material";

function LocationGrid({ handleEditOpen }) {
  const { palette } = useTheme();
  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");

  const [searchInput, setSearchInput] = useState("");

  const dispatch = useDispatch();

  const locations = useSelector((state) => state.locations.data || []);
  const totalLocations = useSelector((state) => state.locations.total || 0);

  useEffect(() => {
    const filters = {
      page, // Ensure it matches API expectations
      limit: pageSize, // API expects 'limit', not 'pageSize'
      sort,
      search,
    };
    dispatch(fetchLocations(filters));
  }, [page, pageSize, sort, search, dispatch]);

  const columns = [
    { field: "name", headerName: "Location Name", flex: 1 },

    { field: "address", headerName: "Address", flex: 1 },
    { field: "city", headerName: "City", flex: 1 },
    { field: "postCode", headerName: "Post Code", flex: 1 },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      renderCell: (params) => moment(params.value).format("ll"),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      renderCell: (params) => moment(params.value).format("ll"),
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 0.5,
      renderCell: (params) => (
        <button
          onClick={() => handleEditOpen(params.row)}
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <EditOutlined sx={{ color: primaryDark }} />
        </button>
      ),
    },
  ];

  // When the user stops typing for 500ms, actually apply the search:
  useEffect(() => {
    const h = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(h);
  }, [searchInput, setSearch, setPage]);

  return (
    <Box
      mt="40px"
      sx={{
        "& .MuiDataGrid-root": {},
        "& .MuiDataGrid-cell": {
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "visible",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: backgroundAlt,
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: backgroundAlt,
        },
        "& .MuiDataGrid-footerContainer": {
          backgroundColor: primaryLight,
        },
        "& .MuiDataGrid-row": {
          height: "auto", // This ensures the row height adapts to content
        },
      }}
    >
      <DataGrid
        getRowId={(row) => row._id}
        rows={locations}
        columns={columns}
        page={page - 1} // Ensure consistency if API expects zero-based pagination
        pageSize={pageSize}
        rowCount={totalLocations}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        onPageChange={(newPage) => {
          setPage(newPage + 1); // Ensure alignment with API expectations
        }}
        onPageSizeChange={(newPageSize) => {
          setPage(1);
          setPageSize(newPageSize);
        }}
        paginationMode="server"
        components={{
          Toolbar: DataGridCustomToolbar,
        }}
        componentsProps={{
          toolbar: {
            search,
            setSearch,
            searchInput,
            setSearchInput,
            sort,
            setSort,
            showSearchBar: false,
          },
        }}
        disableSelectionOnClick
        rowHeight={50}
        autoHeight
      />
    </Box>
  );
}

export default LocationGrid;
